import React, {useEffect, useState} from 'react';
import { Card, Col, Empty, message, Modal, Radio, Row, Space, Table, Tooltip, Typography} from 'antd';
import {DeleteOutlined, EnvironmentOutlined, MinusOutlined, PlusOutlined, QrcodeOutlined, ShoppingOutlined} from '@ant-design/icons';
import styled from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {useCart} from '../../contexts/ShopContext';
import {moneyMask} from '../../utils/utils';
import {formatPath, ROUTES} from '../../constants/PathRoutes';
import {GREE_1} from '../../utils/colors';
import PrescriptionBadge from '../../components/shop/PrescriptionBadge';
import PriceChangedModal from '../../components/shop/PriceChangedModal';
import OrderConfirmModal from '../../components/shop/OrderConfirmModal';
import api from '../../services/api';
import ENDPOINTS from '../../constants/endpoints';
import useDefaultRequest from '../../hooks/useDefaultRequest';
import Button from "../../components/data_entry/Button";

const QtyButton = styled(Button)`
    width: 28px;
    min-width: 28px;
    height: 28px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const TotalSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
`;

function extractApiErrors(error) {
    const data = error?.response?.data;
    if (!data) return {type: 'unknown', messages: ['Erro inesperado.']};
    if (data.shipping_cost_changed) return {type: 'shipping_cost_changed', current: data.shipping_cost_changed.current, messages: [data.shipping_cost_changed.detail]};
    if (data.cart) return {type: 'cart', messages: data.cart};
    if (data.address_id) return {type: 'address_id', messages: data.address_id};
    if (data.non_field_errors) return {type: 'non_field_errors', messages: data.non_field_errors};
    return {type: 'unknown', messages: ['Erro inesperado.']};
}

export default function ShopCart() {
    const navigate = useNavigate();
    const {cartItems, cart, fetchCart, updateQuantity, removeFromCart, clearCart} = useCart();
    const [priceChangedItems, setPriceChangedItems] = useState([]);
    const [syncLoading, setSyncLoading] = useState(false);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('pix');
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [shippingOverride, setShippingOverride] = useState(null);

    const {data: addresses = []} = useDefaultRequest({
        url: ENDPOINTS.AUTH_ADDRESS_LIST,
        params: {page_size: 99},
        hasPagination: true,
        dataDefault: [],
    });
    const {data: rates = []} = useDefaultRequest({url: ENDPOINTS.ECOMMERCE_SHIPPING_RATES_PUBLIC, dataDefault: []});

    useEffect(() => {
        fetchCart().then((data) => {
            const changed = (data?.items || []).filter(item => item.price_changed);
            if (changed.length > 0) setPriceChangedItems(changed);
        });
    }, []);

    const selectedAddress = addresses.find(a => a.id === selectedAddressId);
    const shippingRate = selectedAddress
        ? rates.find(r => r.state_code === selectedAddress.state)
        : null;
    const shippingPrice = shippingOverride ?? (shippingRate ? parseFloat(shippingRate.price) : null);

    const handleSyncPrices = async () => {
        setSyncLoading(true);
        try {
            await api.post(ENDPOINTS.ECOMMERCE_CART_SYNC_PRICES);
            setPriceChangedItems([]);
            fetchCart();
        } finally {
            setSyncLoading(false);
        }
    };

    const handleAddressChange = (id) => {
        setSelectedAddressId(id);
        setShippingOverride(null);
    };

    const handleFinalize = () => {
        setConfirmModalOpen(true);
    };

    const handleConfirmOrder = async () => {
        setConfirmLoading(true);
        try {
            const previewBody = {address_id: selectedAddressId};
            if (shippingPrice !== null) previewBody.expected_shipping_cost = shippingPrice.toFixed(2);
            await api.post(ENDPOINTS.ECOMMERCE_ORDER_PREVIEW, previewBody);
            const {data: order} = await api.post(ENDPOINTS.ECOMMERCE_ORDER_CREATE, {address_id: selectedAddressId});
            setConfirmModalOpen(false);
            clearCart();
            navigate(formatPath(ROUTES.LOJA_PEDIDO_PIX, {id: order.id}));
        } catch (error) {
            setConfirmModalOpen(false);
            const {type, messages: msgs, current} = extractApiErrors(error);
            if (type === 'shipping_cost_changed') {
                setShippingOverride(parseFloat(current));
                message.warning(msgs[0]);
            } else {
                msgs.forEach(msg => message.error(msg));
                fetchCart().then((data) => {
                    const changed = (data?.items || []).filter(item => item.price_changed);
                    setPriceChangedItems(changed);
                });
            }
        } finally {
            setConfirmLoading(false);
        }
    };

    if (cartItems.length === 0) {
        return (
            <Empty
                image={<ShoppingOutlined style={{fontSize: 64, color: '#ccc'}}/>}
                description="Seu carrinho está vazio"
                style={{marginTop: 48}}
            >
                <Button type="primary" onClick={() => navigate(ROUTES.LOJA_PRODUTOS)}>
                    Ver Produtos
                </Button>
            </Empty>
        );
    }

    const confirmRemove = (item) => {
        Modal.confirm({
            title: 'Remover item',
            content: `Deseja remover "${item.product_name}" do carrinho?`,
            okText: 'Sim, remover',
            cancelText: 'Cancelar',
            okButtonProps: {danger: true},
            onOk: () => removeFromCart(item.id),
        });
    };

    const columns = [
        {
            title: 'Produto',
            key: 'product',
            render: (_, item) => (
                <Space>
                    {item.product_image && (
                        <img
                            src={item.product_image}
                            alt={item.product_name}
                            style={{width: 50, height: 50, objectFit: 'cover', borderRadius: 4}}
                        />
                    )}
                    <Space direction="vertical" size={4}>
                        <Typography.Text strong>{item.product_name}</Typography.Text>
                        {item.requires_prescription && <PrescriptionBadge/>}
                    </Space>
                </Space>
            ),
        },
        {
            title: 'Preço Unit.',
            key: 'current_price',
            render: (_, item) => item.discount_percentage ? (
                <Space direction="vertical" size={0}>
                    <Typography.Text delete style={{color: '#999', fontSize: 12}}>
                        R$ {moneyMask(item.current_price)}
                    </Typography.Text>
                    <Typography.Text strong style={{color: GREE_1}}>
                        R$ {moneyMask(item.discounted_price)}
                    </Typography.Text>
                    <Typography.Text style={{color: '#fa8c16', fontSize: 11}}>
                        -{item.discount_percentage}% receita
                    </Typography.Text>
                </Space>
            ) : `R$ ${moneyMask(item.current_price)}`,
        },
        {
            title: 'Quantidade',
            key: 'quantity',
            render: (_, item) => (
                <Tooltip title={item.requires_prescription ? 'Quantidade controlada por prescrição médica' : ''}>
                    <Space>
                        <QtyButton
                            icon={<MinusOutlined/>}
                            disabled={item.requires_prescription}
                            onClick={() => item.quantity === 1 ? confirmRemove(item) : updateQuantity(item.id, item.quantity - 1)}
                        />
                        <Typography.Text>{item.quantity}</Typography.Text>
                        <QtyButton
                            icon={<PlusOutlined/>}
                            disabled={item.requires_prescription}
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        />
                    </Space>
                </Tooltip>
            ),
        },
        {
            title: 'Subtotal',
            dataIndex: 'subtotal',
            key: 'subtotal',
            render: (subtotal) => (
                <Typography.Text strong style={{color: GREE_1}}>
                    R$ {moneyMask(subtotal)}
                </Typography.Text>
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 50,
            render: (_, item) => (
                <Button
                    danger
                    icon={<DeleteOutlined/>}
                    size="small"
                    onClick={() => confirmRemove(item)}
                />
            ),
        },
    ];

    const grandTotal = shippingPrice !== null
        ? parseFloat(cart.total) + shippingPrice
        : parseFloat(cart.total);

    return (
        <>
            <PriceChangedModal
                items={priceChangedItems}
                loading={syncLoading}
                onConfirm={handleSyncPrices}
            />
            <OrderConfirmModal
                open={confirmModalOpen}
                items={cartItems}
                cart={cart}
                address={selectedAddress}
                shippingPrice={shippingPrice}
                paymentMethod={selectedPaymentMethod}
                loading={confirmLoading}
                onConfirm={handleConfirmOrder}
                onCancel={() => setConfirmModalOpen(false)}
            />
            <Row gutter={[16, 24]}>
                <Col xs={24}>
                    <Typography.Title level={4} style={{margin: 0}}>Meu Carrinho</Typography.Title>
                </Col>
                <Col xs={24}>
                    <Table
                        dataSource={cartItems}
                        columns={columns}
                        rowKey="id"
                        pagination={false}
                    />
                </Col>

                <Col xs={24}>
                    <Card
                        title={
                            <Space>
                                <EnvironmentOutlined/>
                                Endereço de entrega
                            </Space>
                        }
                        extra={
                            <Button
                                type="link"
                                size="small"
                                onClick={() => navigate(ROUTES.LOJA_ENDERECOS)}
                            >
                                + Novo endereço
                            </Button>
                        }
                        size="small"
                    >
                        {addresses.length === 0 ? (
                            <Typography.Text type="secondary">
                                Nenhum endereço cadastrado.{' '}
                                <a onClick={() => navigate(ROUTES.LOJA_ENDERECOS)}>Cadastrar endereço</a>
                            </Typography.Text>
                        ) : (
                            <Radio.Group
                                value={selectedAddressId}
                                onChange={e => handleAddressChange(e.target.value)}
                                style={{width: '100%'}}
                            >
                                <Space direction="vertical" style={{width: '100%'}}>
                                    {addresses.map(address => {
                                        const rate = rates.find(r => r.state_code === address.state);
                                        return (
                                            <Radio key={address.id} value={address.id} style={{width: '100%'}}>
                                                <Space direction="vertical" size={0} style={{marginLeft: 4}}>
                                                    <Typography.Text strong>
                                                        {address.street}, {address.number}
                                                        {address.complement ? ` - ${address.complement}` : ''}
                                                    </Typography.Text>
                                                    <Typography.Text type="secondary">
                                                        {address.neighborhood} — {address.city} / {address.state} —
                                                        CEP {address.postal_code}
                                                    </Typography.Text>
                                                    {rate && (
                                                        <Typography.Text style={{color: GREE_1, fontSize: 12}}>
                                                            Frete: R$ {moneyMask(rate.price)}
                                                        </Typography.Text>
                                                    )}
                                                    {!rate && (
                                                        <Typography.Text type="warning" style={{fontSize: 12}}>
                                                            Frete não disponível para este estado
                                                        </Typography.Text>
                                                    )}
                                                </Space>
                                            </Radio>
                                        );
                                    })}
                                </Space>
                            </Radio.Group>
                        )}
                    </Card>
                </Col>

                <Col xs={24}>
                    <Card
                        title={
                            <Space>
                                <QrcodeOutlined/>
                                Forma de pagamento
                            </Space>
                        }
                        size="small"
                    >
                        <Radio.Group
                            value={selectedPaymentMethod}
                            onChange={e => setSelectedPaymentMethod(e.target.value)}
                            style={{width: '100%'}}
                        >
                            <Radio value="pix" style={{width: '100%'}}>
                                <Space style={{marginLeft: 4}}>
                                    <QrcodeOutlined style={{color: GREE_1}}/>
                                    <Space direction="vertical" size={0}>
                                        <Typography.Text strong>Pix</Typography.Text>
                                        <Typography.Text type="secondary" style={{fontSize: 12}}>
                                            Pagamento instantâneo
                                        </Typography.Text>
                                    </Space>
                                </Space>
                            </Radio>
                        </Radio.Group>
                    </Card>
                </Col>

                <Col xs={24}>
                    <TotalSection>
                        <Button type={'default'} danger onClick={() => Modal.confirm({
                            title: 'Limpar carrinho',
                            content: 'Deseja remover todos os itens do carrinho?',
                            okText: 'Sim, limpar',
                            cancelText: 'Cancelar',
                            okButtonProps: {danger: true},
                            onOk: clearCart,
                        })}>
                            Limpar Carrinho
                        </Button>
                        <Space direction="vertical" align="end" size={4}>
                            {cart.discount_total && parseFloat(cart.discount_total) > 0 && (
                                <>
                                    <Typography.Text style={{color: '#999'}}>
                                        Subtotal: R$ {moneyMask(cart.subtotal)}
                                    </Typography.Text>
                                    <Typography.Text style={{color: '#cf1322'}}>
                                        Desconto: - R$ {moneyMask(cart.discount_total)}
                                    </Typography.Text>
                                </>
                            )}
                            {shippingPrice !== null && (
                                <Typography.Text>
                                    Frete: R$ {moneyMask(String(shippingPrice.toFixed(2)))}
                                </Typography.Text>
                            )}
                            {shippingPrice === null && selectedAddress && (
                                <Typography.Text type="secondary" style={{fontSize: 12}}>
                                    Frete não disponível para {selectedAddress.state}
                                </Typography.Text>
                            )}
                            <Typography.Title level={4} style={{margin: 0}}>
                                Total: R$ {moneyMask(String(grandTotal.toFixed(2)))}
                            </Typography.Title>
                            <Button
                                size="large"
                                disabled={!selectedAddressId || !selectedPaymentMethod}
                                style={selectedAddressId && selectedPaymentMethod ? {background: GREE_1, borderColor: GREE_1} : undefined}
                                onClick={handleFinalize}
                            >
                                Finalizar Pedido
                            </Button>
                        </Space>
                    </TotalSection>
                </Col>
            </Row>
        </>
    );
}
